import { aiTranslateMultiple, aiTranslateSingle, getAITranslationStatus } from '@/http/translations';

/**
 * AI Translation Service using backend API
 * Handles translation of Polish text to multiple target languages via API
 */
class AITranslationService {
  constructor() {
    this.supportedLanguages = {
      'en-US': { name: 'English', code: 'en' },
      'hu-HU': { name: 'Hungarian', code: 'hu' },
      'uk-UA': { name: 'Ukrainian', code: 'uk' }
    };
  }

  /**
   * Check if AI translation service is available
   * @returns {Promise<boolean>} Whether the service is available
   */
  async isAvailable() {
    try {
      const response = await getAITranslationStatus();
      return response.data.available;
    } catch (error) {
      console.error('Error checking AI translation status:', error);
      return false;
    }
  }

  /**
   * Translate multiple text entries from Polish to target languages
   * @param {Object} textEntries - Object with key-value pairs where value is Polish text
   * @param {Array} targetLanguages - Array of language codes to translate to (default: all supported)
   * @param {Function} onProgress - Progress callback (language, progress, isComplete)
   * @returns {Promise<Object>} Object with translations organized by key and language
   */
  async translateMultiple(textEntries, targetLanguages = Object.keys(this.supportedLanguages), onProgress = null) {
    if (!textEntries || Object.keys(textEntries).length === 0) {
      throw new Error('No text entries provided for translation');
    }

    const keys = Object.keys(textEntries);
    const totalKeys = keys.length;
    
    // For large batches, use chunked processing
    if (totalKeys > 50) {
      console.log(`Large batch detected (${totalKeys} keys), using chunked processing`);
      return this.translateInChunks(keys, targetLanguages, onProgress);
    }

    try {
      console.log(`Processing ${totalKeys} keys in single request`);
      const response = await aiTranslateMultiple(keys, targetLanguages);
      
      if (onProgress) {
        // Simulate progress completion since API call is atomic
        targetLanguages.forEach((langCode, index) => {
          const langName = this.supportedLanguages[langCode]?.name || langCode;
          onProgress(langName, 1.0, index === targetLanguages.length - 1);
        });
      }

      return response.data.translations;
    } catch (error) {
      console.error('Error in AI translation:', error);
      throw new Error(error.response?.data?.message || 'AI translation failed');
    }
  }

  /**
   * Process translations in chunks for large batches
   * @param {Array} keys - Array of translation keys
   * @param {Array} targetLanguages - Target language codes
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Combined translations
   */
  async translateInChunks(keys, targetLanguages, onProgress = null) {
    const chunkSize = 50; // Process 50 keys at a time
    const chunks = [];
    
    // Split keys into chunks
    for (let i = 0; i < keys.length; i += chunkSize) {
      chunks.push(keys.slice(i, i + chunkSize));
    }

    console.log(`Processing ${keys.length} keys in ${chunks.length} chunks of ${chunkSize}`);

    let allTranslations = {};
    let processedChunks = 0;

    for (const chunk of chunks) {
      try {
        console.log(`Processing chunk ${processedChunks + 1}/${chunks.length} (${chunk.length} keys)`);
        
        const response = await aiTranslateMultiple(chunk, targetLanguages);
        
        // Merge chunk results
        const chunkTranslations = response.data.translations;
        Object.keys(chunkTranslations).forEach(key => {
          allTranslations[key] = chunkTranslations[key];
        });

        processedChunks++;

        // Update progress
        if (onProgress) {
          const progress = processedChunks / chunks.length;
          const isComplete = processedChunks === chunks.length;
          onProgress(`Chunk ${processedChunks}/${chunks.length}`, progress, isComplete);
        }

        // Small delay between chunks to prevent overwhelming the server
        if (processedChunks < chunks.length) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
        }

      } catch (error) {
        console.error(`Error processing chunk ${processedChunks + 1}:`, error);
        throw new Error(`Failed at chunk ${processedChunks + 1}: ${error.message}`);
      }
    }

    console.log(`Chunked processing completed: ${Object.keys(allTranslations).length} keys translated`);
    return allTranslations;
  }

  /**
   * Translate a single key using AI
   * @param {string} key - Translation key
   * @param {string} polishText - Polish text to translate
   * @param {Array} targetLanguages - Array of target language codes
   * @returns {Promise<Object>} Object with translations organized by key and language
   */
  async translateSingleKey(key, _polishText, targetLanguages = Object.keys(this.supportedLanguages)) {
    try {
      // Note: polishText is not used as the API fetches it from the database
      const response = await aiTranslateSingle(key, targetLanguages);
      return response.data.translations;
    } catch (error) {
      console.error('Error in single AI translation:', error);
      throw new Error(error.response?.data?.message || 'AI translation failed');
    }
  }

  /**
   * Get supported languages
   * @returns {Object} Supported languages configuration
   */
  getSupportedLanguages() {
    return { ...this.supportedLanguages };
  }

  /**
   * Check if a language is supported
   * @param {string} languageCode - Language code to check
   * @returns {boolean} Whether the language is supported
   */
  isLanguageSupported(languageCode) {
    return languageCode in this.supportedLanguages;
  }
}

// Export singleton instance
export default new AITranslationService();